import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { getCoinInfo } from '../data/coinDatabase';
import Sparkline from './Sparkline';
import {
    LuX,
    LuTrendingUp,
    LuTrendingDown,
    LuExternalLink
} from 'react-icons/lu';

function CoinDetailModal({ coin, onClose }) {
    const { lang } = useLanguage();

    if (!coin) return null;

    const coinInfo = getCoinInfo(coin.symbol);

    const formatPrice = (price) => {
        if (price >= 1000) return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        if (price >= 1) return `$${price.toFixed(2)}`;
        if (price >= 0.01) return `$${price.toFixed(4)}`;
        return `$${price.toFixed(8)}`;
    };

    const formatVolume = (volume) => {
        if (volume >= 1e9) return `$${(volume / 1e9).toFixed(2)}B`;
        if (volume >= 1e6) return `$${(volume / 1e6).toFixed(2)}M`;
        if (volume >= 1e3) return `$${(volume / 1e3).toFixed(2)}K`;
        return `$${volume.toFixed(2)}`;
    };

    const getCoinIcon = (shortName) => {
        return `https://assets.coincap.io/assets/icons/${shortName.toLowerCase()}@2x.png`;
    };

    const handleIconError = (e) => {
        e.target.style.display = 'none';
        e.target.nextSibling.style.display = 'flex';
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="modal-overlay" onClick={handleOverlayClick}>
            <div className="coin-detail-modal">
                <button className="modal-close-btn" onClick={onClose}>
                    <LuX />
                </button>

                <div className="modal-header">
                    <div className="coin-header-info">
                        <img
                            src={getCoinIcon(coin.shortName)}
                            alt={coin.name}
                            className="modal-coin-icon"
                            onError={handleIconError}
                        />
                        <span className="modal-coin-fallback">{coin.shortName.slice(0, 2)}</span>
                        <div>
                            <h2>{coin.name}</h2>
                            <span className="coin-symbol-badge">{coin.shortName}</span>
                            {coinInfo?.category && (
                                <span className="coin-category-badge">{coinInfo.category}</span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="modal-price-section">
                    <div className="modal-current-price">
                        {formatPrice(coin.price)}
                    </div>
                    <div className={`modal-price-change ${coin.priceChangePercent >= 0 ? 'positive' : 'negative'}`}>
                        {coin.priceChangePercent >= 0 ? <LuTrendingUp /> : <LuTrendingDown />}
                        {coin.priceChangePercent >= 0 ? '+' : ''}{coin.priceChangePercent.toFixed(2)}%
                        <span className="price-change-amount">
                            ({coin.priceChange >= 0 ? '+' : ''}{formatPrice(Math.abs(coin.priceChange))})
                        </span>
                    </div>
                </div>

                <div className="modal-sparkline">
                    <Sparkline data={coin.priceHistory} width={280} height={60} />
                </div>

                <div className="modal-stats">
                    <div className="stat-row">
                        <span className="stat-label">{lang === 'tr' ? '24s Yüksek' : '24h High'}</span>
                        <span className="stat-value high">{formatPrice(coin.high24h)}</span>
                    </div>
                    <div className="stat-row">
                        <span className="stat-label">{lang === 'tr' ? '24s Düşük' : '24h Low'}</span>
                        <span className="stat-value low">{formatPrice(coin.low24h)}</span>
                    </div>
                    <div className="stat-row">
                        <span className="stat-label">{lang === 'tr' ? '24s Hacim' : '24h Volume'}</span>
                        <span className="stat-value">{formatVolume(coin.quoteVolume)}</span>
                    </div>
                    <div className="stat-row">
                        <span className="stat-label">{lang === 'tr' ? 'İşlem Adedi' : 'Trades'}</span>
                        <span className="stat-value">{coin.volume?.toLocaleString() || '-'}</span>
                    </div>
                </div>

                <div className="modal-actions">
                    <a
                        href={`https://www.binance.com/tr/trade/${coin.shortName}_USDT`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="binance-link"
                    >
                        <LuExternalLink />
                        {lang === 'tr' ? "Binance'da İşlem Yap" : 'Trade on Binance'}
                    </a>
                </div>
            </div>
        </div>
    );
}

export default CoinDetailModal;
